// Age bounds for a learner profile. Mirrors Student::AGE_RANGE on the server —
// a learner is not necessarily a schoolchild, so the range covers anyone
// plausibly learning and only rules out obvious typos.
export const AGE_MIN = 3;
export const AGE_MAX = 120;

// How long a session may be, mirroring Setting::MIN/MAX_EXERCISE_COUNT. A
// learner's own default is optional: left empty, the app-wide setting decides.
export const EXERCISE_COUNT_MIN = 1;
export const EXERCISE_COUNT_MAX = 100;

/**
 * Round session lengths offered as one tap. They are a shortcut, never the
 * choice itself — any number in range can be typed instead.
 */
export const EXERCISE_COUNT_PRESETS = [5, 8, 10, 12, 15, 20];

/**
 * A typed session length, or null while the field is empty or not a number.
 * Clamped here so the number a grown-up sees is the number that gets generated
 * — the server clamps too, and silently, which reads as the field being ignored.
 */
export function parseExerciseCount(raw: string): number | null {
  const parsed = parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(Math.max(parsed, EXERCISE_COUNT_MIN), EXERCISE_COUNT_MAX);
}

/**
 * Only shown for the instant before the server's suggestion lands. Mirrors
 * Setting::FALLBACK_EXERCISE_COUNT.
 */
export const EXERCISE_COUNT_FALLBACK = 10;

/** Session lengths a learner picks from. Inside the server's clamp (Setting::MIN/MAX). */
export const EXERCISE_COUNT_CHOICES = [5, 10, 15, 20];

/**
 * Where a learner's own deliberate choice of session length is remembered.
 * Shared because two screens offer to start a session — the kid home and the
 * finish screen — and "one more like that" must mean the same length in both.
 */
export const exerciseCountKey = (studentId: string) => `kid:exerciseCount:${studentId}`;

/** The remembered choice, or null when they have never made one. */
export function rememberedExerciseCount(studentId: string | undefined): number | null {
  if (!studentId) return null;
  const remembered = Number(localStorage.getItem(exerciseCountKey(studentId)));
  return EXERCISE_COUNT_CHOICES.includes(remembered) ? remembered : null;
}

/** Lithuanian counts: 1 užduotis, 2–9 užduotys, 10–20 užduočių. */
export function exercisesLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (last === 1 && lastTwo !== 11) return `${count} užduotis`;
  if (last === 0 || (lastTwo >= 11 && lastTwo <= 19)) return `${count} užduočių`;
  return `${count} užduotys`;
}

/** Lithuanian counts: 1 taškas, 2–9 taškai, 10–20 taškų. */
export function pointsLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (last === 1 && lastTwo !== 11) return `${count} taškas`;
  if (last === 0 || (lastTwo >= 11 && lastTwo <= 19)) return `${count} taškų`;
  return `${count} taškai`;
}

/** Lithuanian counts: 1 įgūdis, 2–9 įgūdžiai, 10–20 įgūdžių. */
export function skillsLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (last === 1 && lastTwo !== 11) return `${count} įgūdis`;
  if (last === 0 || (lastTwo >= 11 && lastTwo <= 19)) return `${count} įgūdžių`;
  return `${count} įgūdžiai`;
}

/** Lithuanian counts: 1 diena, 2–9 dienos, 10–20 dienų. */
export function daysLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (last === 1 && lastTwo !== 11) return `${count} diena`;
  if (last === 0 || (lastTwo >= 11 && lastTwo <= 19)) return `${count} dienų`;
  return `${count} dienos`;
}

export const EXERCISE_COUNT_HINT =
  "Kiek užduočių turi būti mokinio sesijoje. Palikus tuščią, naudojamas bendras nustatymas.";

// The form sends 0 to mean "no preference" — an empty field must not be read as
// a session of zero exercises.
export function exerciseCountValue(raw: FormDataEntryValue | null): number {
  const parsed = parseInt((raw as string) ?? "", 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** One subject's session-length field, as the learner form names it. */
export const subjectExerciseCountField = (subjectKey: string) =>
  `subjectExerciseCount:${subjectKey}`;

/**
 * The per-subject limits a submitted learner form is asking for.
 *
 * Driven by the subjects the form actually *rendered*, never by what the form
 * data happens to contain: the server merges rather than replaces, so a subject
 * left out is a subject left alone — and an emptied box has to come back as 0
 * (clear this one) rather than silently disappearing.
 */
export function subjectExerciseCounts(
  formData: FormData,
  subjectKeys: string[]
): { subjectKey: string; count: number }[] {
  return subjectKeys.map((subjectKey) => ({
    subjectKey,
    count: exerciseCountValue(formData.get(subjectExerciseCountField(subjectKey))),
  }));
}

// Lithuanian schooling starts grade 1 at age 7 and the curriculum ends at 12,
// past which a grade says nothing more about what to offer.
const AGE_AT_GRADE_ONE = 7;
const FINAL_GRADE = 12;

// Everything below mirrors Student in Ruby, so a form can show the age and
// grade it is about to save before saving it. The server stays the authority —
// this only spares a round trip to find out you typed the wrong year.

// A school year is named for the autumn it begins in, and the grade rolls over
// in June rather than September: a child who finished fourth grade in May is,
// to everyone talking about them over the summer, going into fifth.
const SCHOOL_YEAR_ROLLS_OVER_IN = 6;

export function currentSchoolYear(now = new Date()): number {
  return now.getMonth() + 1 >= SCHOOL_YEAR_ROLLS_OVER_IN ? now.getFullYear() : now.getFullYear() - 1;
}

/**
 * As much of a birth date as anybody knows. The parts are a prefix — a year on
 * its own is a perfectly good answer, and it is the only part the app reads.
 */
export type BirthParts = {
  year: number | null;
  month: number | null;
  day: number | null;
};

/**
 * How old they are, to whatever precision was given, or null with no year.
 *
 * A year alone gives the age they turn this calendar year. See Student#age in
 * Ruby for why that is the better answer here and not a rounding error: a school
 * cohort is a birth year.
 */
export function ageFromParts({ year, month, day }: BirthParts, now = new Date()): number | null {
  if (!year) return null;

  let years = now.getFullYear() - year;
  if (month) {
    const monthDiff = now.getMonth() + 1 - month;
    if (monthDiff < 0 || (monthDiff === 0 && day != null && now.getDate() < day)) years -= 1;
  }
  return years;
}

export function gradeFromFirstGradeYear(firstGradeYear: number, now = new Date()): number {
  return Math.min(Math.max(currentSchoolYear(now) - firstGradeYear + 1, 1), FINAL_GRADE);
}

// The school year the child turns 7 — the usual answer, offered so the field can
// be filled in one tap. A child who started early or late is why it is offered
// rather than assumed.
export function suggestedFirstGradeYear(birthYear: number): number {
  return birthYear + AGE_AT_GRADE_ONE;
}

/** How many days that month has, so 31 February is never offered. */
export function daysInMonth(year: number | null, month: number): number {
  if (month === 2) {
    if (!year) return 29;
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Nominative, because they are read on their own in a picker, not in a date. */
export const MONTH_NAMES = [
  "sausis",
  "vasaris",
  "kovas",
  "balandis",
  "gegužė",
  "birželis",
  "liepa",
  "rugpjūtis",
  "rugsėjis",
  "spalis",
  "lapkritis",
  "gruodis",
];

/** "5 klasė". Lithuanian keeps klasė in the nominative after any numeral here. */
export function gradeLabel(grade: number): string {
  return `${grade} klasė`;
}

// "8 metai", "11 metų", "21 metai", "40 metų". Lithuanian takes the genitive
// plural for a round ten and for anything in the 11–19 band of a hundred, and
// the nominative plural everywhere else — so the number decides the word, and
// hardcoding either one is wrong for most ages.
export function yearsWord(age: number): string {
  const withinHundred = Math.abs(Math.trunc(age)) % 100;
  if (withinHundred >= 11 && withinHundred <= 19) return "metų";

  return withinHundred % 10 === 0 ? "metų" : "metai";
}

export function ageLabel(age: number): string {
  return `${age} ${yearsWord(age)}`;
}
